import { useEffect, useState } from 'react';
import { useTheme } from '@/App';
import { Users, TrendingUp, AlertCircle, XCircle, Crown, Award, Lock, DollarSign, Globe } from 'lucide-react';

const SESSION_KEY = 'fh_admin_key';

interface AdminStats {
  total: number;
  newLast7: number;
  newLast30: number;
  trialing: number;
  active: number;
  past_due: number;
  cancelled: number;
  junior: number;
  lead: number;
  mrr: number;
  countryBreakdown: { country: string; count: number }[];
  recentSignups: { name: string; email: string; joined: string; lastSignIn: string | null; status: string | null; plan: string | null; country: string | null; daysLeft: number | null }[];
}

function StatCard({ label, value, sub, color, icon: Icon }: {
  label: string; value: number | string; sub?: string; color: string; icon: any;
}) {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-2xl p-5 flex flex-col gap-3 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-black/5'}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isDark ? 'text-white/40' : 'text-[#1A1F36]/40'}`}>{label}</span>
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <span className={`text-4xl font-black tracking-tight ${isDark ? 'text-white' : 'text-[#1A1F36]'}`}>{value}</span>
      {sub && <span className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>{sub}</span>}
    </div>
  );
}

async function fetchStats(key: string): Promise<AdminStats> {
  const res = await fetch('/api/admin-stats', { headers: { 'x-admin-key': key } });
  if (!res.ok) throw new Error('Forbidden');
  return res.json();
}

export default function AdminPage() {
  const { isDark } = useTheme();
  const [key, setKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Restore session from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      setLoading(true);
      fetchStats(saved)
        .then(data => { setStats(data); setAuthed(true); setLoading(false); })
        .catch(() => { sessionStorage.removeItem(SESSION_KEY); setLoading(false); });
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await fetchStats(key);
      sessionStorage.setItem(SESSION_KEY, key);
      setStats(data);
      setAuthed(true);
    } catch {
      setError('Incorrect password.');
    }
    setLoading(false);
  };

  const bg = isDark ? 'bg-[#0f1419]' : 'bg-[#E3E8F1]';
  const text = isDark ? 'text-white' : 'text-[#1A1F36]';
  const muted = isDark ? 'text-white/40' : 'text-[#1A1F36]/40';
  const cardBg = isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5';
  const inputCls = `w-full px-4 py-3 rounded-2xl border text-sm font-bold outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-[#4A6CF7]' : 'bg-white border-black/10 text-[#1A1F36] placeholder-black/20 focus:border-[#4A6CF7]'}`;

  if (!authed) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center p-6`}>
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto ${isDark ? 'bg-white/10' : 'bg-black/5'}`}>
              <Lock className={`w-5 h-5 ${muted}`} />
            </div>
            <h1 className={`text-2xl font-black tracking-tight ${text}`}>Admin</h1>
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${muted}`}>FirstHold</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-3">
            <input
              type="password"
              placeholder="Password"
              value={key}
              onChange={e => setKey(e.target.value)}
              className={inputCls}
              autoFocus
            />
            {error && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500 text-center">{error}</p>}
            <button
              type="submit"
              disabled={loading || !key}
              className="w-full py-3 bg-[#4A6CF7] text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:opacity-90 transition-all disabled:opacity-40"
            >
              {loading ? 'Checking…' : 'Enter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg} p-4 md:p-8`}>
      <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1 className={`text-2xl md:text-3xl font-black tracking-tight ${text}`}>Admin</h1>
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${muted}`}>FirstHold — User Funnel</p>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem(SESSION_KEY); setAuthed(false); setStats(null); setKey(''); }}
            className={`text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-xl transition-all ${isDark ? 'text-white/30 hover:text-white hover:bg-white/10' : 'text-[#1A1F36]/30 hover:text-[#1A1F36] hover:bg-black/5'}`}
          >
            Lock
          </button>
        </div>

        {loading && <p className={`text-sm ${muted}`}>Loading…</p>}

        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <StatCard label="Total Users" value={stats.total} icon={Users} color="bg-[#4A6CF7]/20 text-[#4A6CF7]" />
              <StatCard label="New (7 days)" value={stats.newLast7} icon={TrendingUp} color="bg-purple-500/20 text-purple-400" />
              <StatCard label="New (30 days)" value={stats.newLast30} icon={TrendingUp} color="bg-purple-500/20 text-purple-400" />
              <StatCard label="Est. MRR" value={`$${stats.mrr.toFixed(2)}`} icon={DollarSign} color="bg-emerald-500/20 text-emerald-400" sub="USD · active subs only" />
            </div>

            <div>
              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${muted}`}>Subscription Status</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <StatCard label="Trialing" value={stats.trialing} icon={TrendingUp} color="bg-amber-500/20 text-amber-400"
                  sub={stats.total > 0 ? `${Math.round(stats.trialing / stats.total * 100)}% of users` : undefined} />
                <StatCard label="Active" value={stats.active} icon={Award} color="bg-emerald-500/20 text-emerald-400"
                  sub={stats.total > 0 ? `${Math.round(stats.active / stats.total * 100)}% of users` : undefined} />
                <StatCard label="Past Due" value={stats.past_due} icon={AlertCircle} color="bg-red-500/20 text-red-400" />
                <StatCard label="Cancelled" value={stats.cancelled} icon={XCircle} color="bg-slate-500/20 text-slate-400" />
              </div>
            </div>

            <div>
              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${muted}`}>Plan Breakdown</p>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <StatCard label="Junior" value={stats.junior} icon={Award} color="bg-[#4A6CF7]/20 text-[#4A6CF7]"
                  sub={stats.total > 0 ? `${Math.round(stats.junior / stats.total * 100)}% of users` : undefined} />
                <StatCard label="Lead" value={stats.lead} icon={Crown} color="bg-amber-500/20 text-amber-400"
                  sub={stats.total > 0 ? `${Math.round(stats.lead / stats.total * 100)}% of users` : undefined} />
              </div>
            </div>

            {stats.countryBreakdown.length > 0 && (
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${muted}`}>Active Subscribers by Country</p>
                <div className={`rounded-2xl border p-4 md:p-5 flex flex-wrap gap-3 ${cardBg}`}>
                  {stats.countryBreakdown.map(({ country, count }) => (
                    <div key={country} className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${isDark ? 'bg-white/5' : 'bg-black/5'}`}>
                      <Globe className={`w-3 h-3 ${muted}`} />
                      <span className={`text-xs font-bold ${text}`}>{country}</span>
                      <span className={`text-xs font-black text-[#4A6CF7]`}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${muted}`}>Recent Signups</p>

              {/* Mobile: card list */}
              <div className={`md:hidden rounded-2xl border overflow-hidden divide-y ${cardBg} ${isDark ? 'divide-white/5' : 'divide-black/5'}`}>
                {stats.recentSignups.length === 0 ? (
                  <p className={`p-5 text-sm ${muted}`}>No signups yet.</p>
                ) : stats.recentSignups.map((u, i) => (
                  <div key={i} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-sm font-bold ${text}`}>{u.name}</p>
                        <p className={`text-xs ${muted}`}>{u.email}</p>
                      </div>
                      <div className="flex-shrink-0">
                        {u.status === 'trialing' ? (
                          <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-amber-500/20 text-amber-500">Trial</span>
                        ) : u.status === 'active' ? (
                          <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 text-emerald-500">Active</span>
                        ) : u.status ? (
                          <span className={`text-[10px] font-bold uppercase tracking-widest ${muted}`}>{u.status.replace('_', ' ')}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className={`flex flex-wrap gap-x-4 gap-y-1 text-[11px] ${muted}`}>
                      <span>Joined {new Date(u.joined).toLocaleDateString()}</span>
                      <span>Last in {u.lastSignIn ? new Date(u.lastSignIn).toLocaleDateString() : '—'}</span>
                      {u.plan && <span className={`font-black uppercase ${u.plan === 'lead' ? 'text-amber-500' : 'text-[#4A6CF7]'}`}>{u.plan}</span>}
                      {u.country && <span>{u.country}</span>}
                      {u.status === 'trialing' && u.daysLeft !== null && (
                        <span className={u.daysLeft <= 3 ? 'text-red-400 font-bold' : ''}>{u.daysLeft > 0 ? `${u.daysLeft}d left` : 'expired'}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className={`hidden md:block rounded-2xl border overflow-hidden ${cardBg}`}>
                {stats.recentSignups.length === 0 ? (
                  <p className={`p-5 text-sm ${muted}`}>No signups yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-white/10' : 'border-black/5'}`}>
                        {['Name', 'Email', 'Joined', 'Last Sign In', 'Plan', 'Country', 'Status'].map(h => (
                          <th key={h} className={`text-left px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] ${muted}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentSignups.map((u, i) => (
                        <tr key={i} className={`border-b last:border-0 ${isDark ? 'border-white/5' : 'border-black/5'}`}>
                          <td className={`px-5 py-3 font-bold ${text}`}>{u.name}</td>
                          <td className={`px-5 py-3 ${muted}`}>{u.email}</td>
                          <td className={`px-5 py-3 ${muted}`}>{new Date(u.joined).toLocaleDateString()}</td>
                          <td className={`px-5 py-3 ${muted}`}>{u.lastSignIn ? new Date(u.lastSignIn).toLocaleDateString() : '—'}</td>
                          <td className={`px-5 py-3`}>
                            {u.plan ? (
                              <span className={`text-[10px] font-black uppercase tracking-widest ${u.plan === 'lead' ? 'text-amber-500' : 'text-[#4A6CF7]'}`}>{u.plan}</span>
                            ) : <span className={muted}>—</span>}
                          </td>
                          <td className={`px-5 py-3 ${muted}`}>{u.country ?? '—'}</td>
                          <td className="px-5 py-3">
                            {u.status === 'trialing' ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-amber-500/20 text-amber-500">Trial</span>
                                {u.daysLeft !== null && (
                                  <span className={`text-[10px] font-bold ${u.daysLeft <= 3 ? 'text-red-400' : muted}`}>
                                    {u.daysLeft > 0 ? `${u.daysLeft}d left` : 'expired'}
                                  </span>
                                )}
                              </span>
                            ) : u.status === 'active' ? (
                              <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 text-emerald-500">Active</span>
                            ) : u.status ? (
                              <span className={`text-[10px] font-bold uppercase tracking-widest ${muted}`}>{u.status.replace('_', ' ')}</span>
                            ) : (
                              <span className={`text-[10px] ${muted}`}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
